import React, { useState, useMemo } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { SelectableShift } from '../../types';
import '../../../../../../styles/Forms.scss';
import './ShiftSelectModal.scss';

interface ShiftSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedShifts: SelectableShift[]) => void;
  shifts: SelectableShift[];
  maxSelections?: number;
}

const ShiftSelectModal: React.FC<ShiftSelectModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  shifts,
  maxSelections = 7,
}) => {
  const [selectedShiftIds, setSelectedShiftIds] = useState<Set<number>>(new Set());

  // Create unique key for each shift to avoid React key conflicts
  const getUniqueShiftKey = (shift: SelectableShift): string => {
    return `${shift.session_id}-${shift.shift_id}`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-CL', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const handleToggleShift = (shiftId: number) => {
    setSelectedShiftIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(shiftId)) {
        newSet.delete(shiftId);
      } else if (newSet.size < maxSelections) {
        newSet.add(shiftId);
      }
      return newSet;
    });
  };

  const handleConfirm = () => {
    const selectedShifts = shifts.filter((s) => selectedShiftIds.has(s.shift_id));
    onConfirm(selectedShifts);
    setSelectedShiftIds(new Set());
  };

  const handleClose = () => {
    setSelectedShiftIds(new Set());
    onClose();
  };

  const totalSelectedTips = useMemo(() => {
    return shifts
      .filter((s) => selectedShiftIds.has(s.shift_id))
      .reduce((sum, s) => sum + s.total_tip, 0);
  }, [shifts, selectedShiftIds]);

  return (
    <Modal
      show={isOpen}
      onHide={handleClose}
      centered
      dialogClassName="shift-select-modal-dialog"
    >
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">Seleccionar Turnos</Modal.Title>
        </div>
        <Button variant="none" className="modal-close-button" onClick={handleClose}>
          <X size={24} />
        </Button>
      </Modal.Header>

      <Modal.Body className="shift-select-body">
        <p className="body-subtitle">
          Selecciona hasta {maxSelections} turnos para distribuir propinas
        </p>

        {shifts.length === 0 ? (
          <div className="empty-state">
            <p>No hay turnos disponibles</p>
          </div>
        ) : (
          <div className="shifts-list">
            {shifts.map((shift) => {
              const isSelected = selectedShiftIds.has(shift.shift_id);
              const isDisabled = !isSelected && selectedShiftIds.size >= maxSelections;

              return (
                <div
                  key={getUniqueShiftKey(shift)}
                  className={`shift-item ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                  onClick={() => !isDisabled && handleToggleShift(shift.shift_id)}
                >
                  <div className="shift-checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      disabled={isDisabled}
                    />
                    <span className="checkmark"></span>
                  </div>

                  <div className="shift-info">
                    <div className="shift-header">
                      <span className="shift-name">{shift.name}</span>
                      <span className="shift-tip">{formatCurrency(shift.total_tip)}</span>
                    </div>
                    
                    <div className="shift-dates">
                      <span className="date-range">
                        {formatDate(shift.start_time)} {formatTime(shift.start_time)}
                        {' → '}
                        {formatDate(shift.end_time)} {formatTime(shift.end_time)}
                      </span>
                    </div>

                    <div className="shift-users">
                      <span className="users-label">Participantes:</span>
                      <span className="users-names">
                        {shift.user_names.length > 0
                          ? shift.user_names.join(', ')
                          : 'Sin participantes'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="shift-select-footer">
          <div className="selection-summary">
            <span className="selection-count">
              {selectedShiftIds.size} de {maxSelections} turnos seleccionados
            </span>
            <span className="selection-total">
              Total propinas: <strong>{formatCurrency(totalSelectedTips)}</strong>
            </span>
          </div>
          
          <div className="form-modal-footer-custom">
            <Button variant="none" className="form-button-cancel" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              variant="none"
              className="form-button-create"
              onClick={handleConfirm}
              disabled={selectedShiftIds.size === 0}
            >
              Confirmar ({selectedShiftIds.size})
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ShiftSelectModal;
